import Arrangement from "../arrangement/Arrangement";
import Maths from "oc-commons/Maths";
import Scenes from "oc-threejs/Scenes";
import Textures from "oc-threejs/Textures";
import UniversalLoader from "oc-threejs/loader/UniversalLoader";
import Commons from "oc-commons/Commons";
import Interieur from "../arrangement/Interieur";
import Schubladenbox from "../arrangement/Schubladenbox";
import CompileError from "./CompileError";
import Tuer from "../arrangement/Tuer";
import Einlegeboden from "../arrangement/Einlegeboden";
import { v3 } from "oc-threejs/short3";
import Trennwandbox from "../arrangement/Trennwandbox";
import InteractionHandler from "../interaction/InteractionHandler";
import Konstruktionsboden from "../arrangement/Konstruktionsboden.js";

/**
 * Das Model ist
 * - das Vater-Objekt3D für das Arrangement
 * - Zentrale der allgemeinen compile - Logik des Arrangements
 */
export default class Model extends THREE.Object3D {
    name = "o3model";

    // dient zum Erkennen von Objekten, die gelöscht werden können
    compilationNumber = 0;

    // für Logausgabe
    createdObjectsDuringCompilation = [];

    cameraExtraZoom = 1.0;
    dirty = false;
    doNotThrowCompileErrors = false;

    importInterieursToRemove: Interieur[] = [];
    importInterieursRemoved = 0;

    lastOkState: ?Arrangement;

    arrangementO3 = null;

    constructor() {
        super();
        this.object3dType = Commons.slurpDirectory(require.context("../object3d/", false));
    }

    compile() {
        const t0 = Date.now();
        this.compilationNumber++;
        this.createdObjectsDuringCompilation = [];
        APP.arrangement.compile();
        APP.trigger("afterCompile");
        this._vsUpdate();
        this._removeUnusedCompilationObjects();

        // if (APP.settings.localDevelopmentMode) {
        //     new ArrangementObjectRelationDebugger().check;
        // }
        const t1 = Date.now();
        // console.log('compile ms:', t1 - t0, 'created:', this.createdObjectsDuringCompilation, 'removed:', removed);
        if (!APP.interactionHandler.isDragging) {
            APP.trigger("changedConfiguration");
        }
    }

    /**
     * Vom Arrangement oder dessen Children während des compile aufgerufen.
     * Erzeugt ein Objekt3D, falls es noch nicht im compiledData vorhanden ist, und verknüpft es mit dem vsData.
     * Ausserdem aktualisiert es die compilationNumber - ansonsten würde das Objekt nämlich durch
     * _removeUnusedCompilationObjects gelöscht werden
     */
    lazyCreateObject3d(compiledData, createCallback) {
        if (!compiledData.object3d) {
            compiledData.object3d = createCallback(compiledData);
            this.createdObjectsDuringCompilation.push(compiledData.object3d);
        }
        compiledData.object3d.compilationNumber = this.compilationNumber;
        compiledData.object3d.vsData = compiledData;

        /**
         * Um sicherzustellen, dass die 03d Objekte richtige Parent/Child Beziehungen haben
         * Notwendig, weil die Funktion InteractionHandler.attachToElement eventuell früher als der BIH3D reagiert
         * Ohne diese Funktion führt schnelles drag&drop zu fehlerhaften Interieuren im Schrank
         */
        if (compiledData.parent?.child3dContainer != undefined && compiledData.parent.child3dContainer !== compiledData.object3d.parent) {
            compiledData.parent.child3dContainer.add(compiledData.object3d);
        }

        return compiledData.object3d;
    }

    /**
     * Vom Arrangement oder dessen Children während des compile aufgerufen.
     * Für Arrays von Arrangement-Child-Objekten, deren Länge sich während des Compile-Vorgangs ändert.
     * Sorgt dafür, dass Objekte möglichst wiederverwendet werden, und räumt bei Bedarf überflüssige Objekte ab.
     * @param compiledEntryParent Vater-Instanz, die das Array enhtält
     * @param arrayName
     * @param compiledEntryCreator Callback, welche die Arrangement-Child-Instanz nach Bedarf erzeugt.
     * @param object3DCreator Callback, welche die Object3D-Instanz nach Bedarf erzeugt.
     * @return {*}
     */
    lazyPool(compiledEntryParent, arrayName, compiledEntryCreator, object3DCreator = null) {
        const array = (compiledEntryParent[arrayName] = compiledEntryParent[arrayName] || []);
        let usedLength = 0;
        const model = this;
        return {
            get(i) {
                usedLength = Math.max(usedLength, i + 1);
                while (i + 1 > array.length) {
                    array.push(compiledEntryCreator());
                }
                const entry = array[i];
                if (object3DCreator) {
                    model.lazyCreateObject3d(entry, object3DCreator);
                }
                return entry;
            },

            next() {
                return this.get(usedLength);
            },

            cleanup() {
                if (array.length > usedLength) {
                    array.splice(usedLength, array.length - usedLength);
                }
            },

            array,
        };
    }

    _vsUpdate(object = this) {
        // vsUpdate hat den einzigen Zweck, den neuen Ist-Zustand des Object3D vollständig aus dem vsData
        // abzuleiten, indem auf geänderten Werte (isVsChanged) reagiert wird. Das Object3D darf auf keinen
        // anderen Weg verändert werden - ausser während eines Dragvorganges! vsUpdate sollte dazu möglichst nur
        // auf Daten des eigenen vsData zugreifen. Insbesondere sollten Parent-Objekte nicht den Zustand ihrer
        // Child-Objekte ändern!

        // Besser: AN dieser stelle findet object.traverse die Tür nicht wenn sie im Schrank buggt.
        // Scheinbar ist sie noch nicht erstellt

        /* let list = [];
        object.traverse((node) => {
            list.push(node);
        });
         console.log(list.filter((l) => {
            let foo = l.vsUpdate !== undefined;
            if(foo === false) {
                l.name === "Doppeltür Standard" ? console.log(l.vsUpdate) : null;
            }
            return foo;
        })); */

        Scenes.filter(object, (o) => o.vsUpdate !== undefined).forEach((o) => o.vsUpdate());
    }

    /**
     * wichtig für Dragvorgang: hiermit wird ein Objekt von der Compilation losgelöst,
     * so dass die Compilation das Objekt nicht mehr versehentlich löscht
     */
    detachFromCompilation(object3d: THREE.Object3D) {
        if (object3d) {
            object3d.traverse((o) => {
                if (o.compilationNumber !== undefined) {
                    o.compilationNumber = null;
                }
            });
        }
    }

    atttachToCompilation(object3d: THREE.Object3D) {
        if (object3d) {
            object3d.traverse((o) => {
                if (o.compilationNumber !== undefined) {
                    o.compilationNumber = this.compilationNumber;
                }
            });
        }
    }

    /**
     * @private
     */
    _removeUnusedCompilationObjects() {
        const toRemove = this._findObjectsToRemove(this);
        for (let o of toRemove) {
            o.traverse((x) => {
                if (x.vsData) {
                    x.vsData.object3d = null;
                    if (x.vsData.delete) {
                        x.vsData.delete();
                    }
                }
            });
            Scenes.remove(o);
        }
        APP.trigger("removedUnusedObjects");
    }

    /**
     * @private
     */
    _findObjectsToRemove(object: THREE.Object3D, toRemove: THREE.Object3D[] = []) {
        // deprecated
        /* if (object.autoRemove) {
            console.log("autoremove enabled for: ", object);
            return toRemove;
        } */
        if (object.compilationNumber != null && object.compilationNumber !== this.compilationNumber) {
            toRemove.push(object);
            return toRemove;
        }
        for (let o of object.children) {
            this._findObjectsToRemove(o, toRemove);
        }
        return toRemove;
    }

    get currentSchrank() {
        return APP.arrangement.schraenke[APP.arrangement.currentSchrankIndex];
    }

    setMaterial(materialArtikelnummer, side, compile = true, interieur) {
        if (compile) {
            APP.model.tryCompile(() =>
                APP.arrangement.schraenke.forEach((schrank) => schrank.setMaterialArtikelnummer(materialArtikelnummer, side, interieur))
            );
        } else {
            APP.arrangement.schraenke.forEach((schrank) => schrank.setMaterialArtikelnummer(materialArtikelnummer, side, interieur));
        }
    }

    loadConfiguration() {
        Scenes.empty(this);

        const product = APP.findProduct("Schrank");
        const json = APP.configuration.Json;
        APP.arrangement.fromJSON(json);
        let tries = 0;
        do {
            this.doNotThrowCompileErrors = true;
            try {
                this.compile();
            } finally {
                this.doNotThrowCompileErrors = false;
            }
            this.importInterieursToRemove.forEach((interieur) => {
                interieur.removeFromElement();
                this.importInterieursRemoved++;
            });
            this.importInterieursToRemove = [];
            tries++;
        } while (tries < 50 || this.importInterieursToRemove.length > 0);

        // JVI noch nicht initialisiert: this.showDoorsClosed($('#tueren').bootstrapSwitch('state'), 0);

        this.setFullCam();

        this.setDirty();
    }

    loadTexture(url, rx = 1, ry = 1) {
        const texture = UniversalLoader.loadTexture(url, null, null, null, true, "");
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        texture.repeat.set(rx, ry);
        texture.anisotropy = Textures.getMaxAnisotropy() / 2;
        return texture;
    }

    setDirty() {
        this.dirty = true;
        STAGE.dirty();
    }

    onBeforeRenderStage() {
        if (this.dirty) {
            this.dirty = false;
            if (APP.interactionHandler.isDragging) {
                let wasError = false;
                const throwBefore = APP.model.doNotThrowCompileErrors;
                try {
                    APP.model.doNotThrowCompileErrors = false;
                    this.compile();
                } catch (e) {
                    if (e instanceof CompileError) {
                        wasError = true;
                    }
                } finally {
                    APP.model.doNotThrowCompileErrors = throwBefore;
                }
                if (wasError && this.lastOkState) {
                    const draggedVsData = APP.interactionHandler.dragObject3d ? APP.interactionHandler.dragObject3d.vsData : null;
                    const draggedId = draggedVsData ? draggedVsData.id : Number.NaN;
                    APP.arrangement.copy(this.lastOkState, (fn, destination, source, parent) => {
                        if (source.id === draggedId) {
                            draggedVsData.element = parent;
                            draggedVsData.element.child3dContainer.add(draggedVsData.object3d);
                            return fn(draggedVsData, source);
                        }
                        return fn(destination, source);
                    });
                    this.compile();
                } else {
                    if (!this.lastOkState) {
                        this.lastOkState = new Arrangement();
                    }
                    this.lastOkState.copy(APP.arrangement, (fn, destination, source, parent) => fn(destination, source));
                }
            } else {
                try {
                    this.compile();
                } catch (e) {
                    console.error("unknown Error! CARE! THIS SITUATION IS CRUCIALLY!", e);
                }
            }
            APP.updateUI();
            this.maybeShowHinweistexteDialog();
        }
    }

    maybeShowHinweistexteDialog() {
        const texte = APP.arrangement.getHinweistexteFormatted();
        if (texte) {
            APP.arrangement.resetHinweistexte();
            if (APP.interactionHandler.isDragging) {
                console.warn("Hinweistexte werden während Dragging bewusst unterdrückt:", texte);
                return;
            }
            APP.uiHandler.dialog({
                title: texte.title,
                text: texte.messages.join("<br/>"),
                buttons: [{ style: "primary", label: I18N("OK") }],
            });
        }
    }

    setFullCam({ object3d = null, extraZoom = this.cameraExtraZoom, direction = v3(0, 0, 1) } = {}) {
        this.updateMatrixWorld(true);
        if (object3d == null) {
            object3d = APP.arrangement.object3d.schraenke;
        }
        STAGE.zoomTo(object3d, direction, extraZoom);
    }

    setBreite(breite, elementId = -1, schrankId = -1) {
        this.tryCompile(() => {
            if (schrankId === -1) {
                APP.arrangement.schraenke.forEach((schrank) => {
                    if (elementId === -1) {
                        schrank.setDimension("x", breite);
                    } else {
                        schrank.elements[elementId].setDimension("x", breite);
                    }
                });
            } else {
                if (elementId === -1) {
                    APP.arrangement.schraenke[schrankId].setDimension("x", breite);
                } else {
                    APP.arrangement.schraenke[schrankId].elements[elementId].setDimension("x", breite);
                }
            }
        });
    }

    setHoehe(hoehe, elementId = -1, schrankId = -1) {
        this.tryCompile(() => {
            if (schrankId === -1) {
                APP.arrangement.schraenke.forEach((schrank) => {
                    if (elementId === -1) {
                        if (schrank.elements.some((e) => !e.locked.y)) {
                            /* console.log(schrank); */
                            schrank.setDimension("y", hoehe);
                        }
                    } else {
                        schrank.elements[elementId].setDimension("y", hoehe);
                    }
                });
            } else {
                if (elementId === -1) {
                    if (APP.arrangement.schraenke[schrankId].elements.some((e) => !e.locked.y)) {
                        APP.arrangement.schraenke[schrankId].setDimension("y", hoehe);
                    }
                } else {
                    APP.arrangement.schraenke[schrankId].elements[elementId].setDimension("y", hoehe);
                }
            }
        });
    }

    setTiefe(tiefe, elementId = -1, schrankId = -1) {
        APP.model.tryCompile(() => {
            if (schrankId === -1) {
                APP.arrangement.schraenke.forEach((schrank) => {
                    schrank.setDimension("z", tiefe);
                });
            } else {
                APP.arrangement.schraenke[schrankId].setDimension("z", tiefe);
            }
        });
    }

    setModell(modell) {
        APP.model.tryCompile(() => {
            this.setModellInternal(modell);
        });
    }

    setModellInternal(modell) {
        APP.arrangement.Modell = _.clone(modell);
        APP.configuration.Json.Modell = _.clone(modell);
        APP.mergeSettings({ metadata: APP.metadata, Konfiguration: APP.configuration });
        APP.arrangement.schraenke[0].angleSide = APP.arrangement.schraenke[0].options.getValue("dachschraege") || "none";
        APP.arrangement.heal();
        APP.model.compileForced();
        APP.model.setFullCam();
        STAGE.settings.lightSettingsName = APP.settings.light_settings_name.Wert;
        STAGE.createLights();
    }

    setKniestock(kniestock) {
        APP.model.tryCompile(() => APP.arrangement.schraenke[0].setDimensionTop("y", kniestock));
    }

    setTiefeOben(tiefeOben) {
        APP.model.tryCompile(() => APP.arrangement.schraenke[0].setDimensionTop("z", tiefeOben));
    }

    setBreiteOben(breiteOben) {
        APP.model.tryCompile(() => APP.arrangement.schraenke[0].setDimensionTop("x", breiteOben));
    }

    setDoorHandle(artikelnummer) {
        APP.model.tryCompile(() => APP.arrangement.setDoorHandle(artikelnummer));
    }

    selectElement(elementId = -1, schrankId = -1) {
        if (schrankId == -1) {
            schrankId = APP.arrangement.currentSchrankIndex;
        }
        APP.model.tryCompile(() => APP.arrangement.schraenke[schrankId].selectElement(elementId));
    }

    showDoorsClosed(close, time = 1000) {
        if (APP.arrangement.doorsClosed == close) {
            return;
        }
        APP.arrangement.doorsClosed = close;

        APP.arrangement.schraenke.forEach((schrank) => {
            if (schrank.isSchwebetuer) {
                schrank.schwebetuer.showClosed(close, time);
            } else {
                schrank.elements.forEach((element) => {
                    element.getDoors().forEach((door) => {
                        door.showClosed(close, time);
                    });
                });
            }
        });

        APP.updateUI();
    }

    showMaßlinien(see, time = 1000) {
        if (APP.arrangement.maße == see) {
            return;
        }
        APP.arrangement.maße = see;

        APP.arrangement.object3d.ruler.vsUpdate(true);
        STAGE.dirty();
        APP.updateUI();
    }

    createInterieur(type, element = null) {
        switch (type) {
            case "Schubladenbox":
                return new Schubladenbox(type, element);
            case "Tuer":
                return new Tuer(type, element);
            case "Trennwandbox":
                return new Trennwandbox(type, element);
            case "Einlegeboden":
                return new Einlegeboden(type, element);
            case "Konstruktionsboden":
                return new Konstruktionsboden(type, element);
        }
        return new Interieur(type, element);
    }

    createObject3D(vsData) {
        const product = vsData.product;
        const typeClassname = product.type ? product.type.substringBefore("#") : null;
        let Type = this.object3dType[typeClassname];
        if (!Type) {
            // Type = this.object3dType['AbstractInterieur']; //CHE: Workaround Laden
            //console.log("createObject3D type not found: " + typeClassname + " Artikelnummer: " + product.Artikelnummer)
            throw "createObject3D type not found: " + typeClassname + " Artikelnummer: " + product.Artikelnummer;
        }
        let object = new Type(vsData);
        object.name = product.Name;
        return object;
    }

    createDragObject(product) {
        const interieur = this.createInterieur(product.type);
        interieur.product = product;
        interieur.compileSizeAndBox();
        interieur.compileInternals();
        interieur.compileFront();
        this._vsUpdate();
        return interieur.object3d;
    }

    maybeThrowCompileError(initiator, title, i18nKey, ...params) {
        console.error("maybeThrowCompileError", initiator, title, i18nKey, params);
        if (initiator instanceof Interieur) {
            this.importInterieursToRemove.push(initiator);
        }
        if (!this.doNotThrowCompileErrors) {
            throw new CompileError(title, i18nKey, ...params);
        }
    }

    throwFastForDebugging = false;

    compileForced() {
        if (APP.model.throwFastForDebugging) {
            APP.model.compile();
            return;
        }
        const throwBefore = APP.model.doNotThrowCompileErrors;
        APP.model.doNotThrowCompileErrors = true;
        try {
            APP.model.compile();
        } finally {
            APP.model.doNotThrowCompileErrors = throwBefore;
        }
    }

    tryCompile(callback, errorCallback) {
        if (this.throwFastForDebugging) {
            callback();
            this.compile();
            this.setDirty();
            return;
        }
        const safepoint = this.createCompilationSafePoint();
        const handleError = (e) => {
            if (errorCallback && e) {
                errorCallback(e);
            } else {
                safepoint.maybeShowError(e);
            }
        };
        try {
            try {
                callback();
                safepoint.compile();
            } catch (e) {
                debugger;
                // Hier kann es passieren, dass Fehler nicht aufgelöst werden können.. oder?
                console.error("Error in tryCompileCallback or afterCompile. Trying rollback!");
                safepoint.rollback();
                if (e instanceof CompileError) {
                    handleError(e);
                } else {
                    console.error(e);
                    throw e;
                }
            }
        } finally {
            this.setDirty();
        }
    }

    tryWrap(reactComponent, callback) {
        return (...args) => {
            this.tryCompile(() => {
                callback(...args);
                if (reactComponent) {
                    reactComponent.forceUpdate();
                }
            });
        };
    }

    createCompilationSafePoint() {
        const backup = Commons.cloneDeep(APP.arrangement.toJSON());
        const altesModell = APP.arrangement.Modell;
        const self = this;

        const model = this;
        return {
            error: null,
            compile() {
                const throwBefore = APP.model.doNotThrowCompileErrors;
                try {
                    APP.model.doNotThrowCompileErrors = false;
                    model.compile();
                    console.log('%csafePoint compiled without errors.', 'background: #fff; color: #006400');
                    return true;
                } catch (e) {
                    if (e instanceof CompileError) {
                        this.error = e;
                    }
                    throw e;
                } finally {
                    APP.model.doNotThrowCompileErrors = throwBefore;
                }
            },
            rollback() {
                APP.arrangement.fromJSON(backup);
                try {
                    APP.arrangement.compile();
                    APP.uiHandler.updateFromArrangement();
                } catch (e) {
                    // Hier sollte schon garkein Fehler mehr auftreten.. Vorher hat es schließlich auch compiled
                    // Anzeichen dafür, dass im fromJSON etwas nicht stimmt - Stand wie vorher kann nicht exakt wiederhergestellt werden
                    if (e instanceof CompileError) {
                        console.warn("error in rollback, will be ignored:", e);
                    } else {
                        throw e;
                    }
                }
            },
            maybeShowError(error) {
                error = this.error || error;
                if (error) {
                    APP.arrangement.setHinweisTextAndTitle(error.title, error.i18nKey, ...error.params);
                    model.maybeShowHinweistexteDialog();
                }
            },
        };
    }
}
